import React, { useState, useRef, useEffect, useCallback } from "react";

const InvestmentSummary = () => {
  const [countedStats, setCountedStats] = useState({
    apartments: 0,
    totalValue: 0,
    years: 0,
    training: 0,
  });
  const [hasAnimated, setHasAnimated] = useState(false);
  const [animatedAmounts, setAnimatedAmounts] = useState({});

  const annualTransactions = [
    { id: 1, year: "2017", country: "ET", amount: 352401304, suffix: "" },
    { id: 2, year: "2017", country: "AE", amount: 221923689, suffix: "" },
    { id: 3, year: "Total close in 2017", country: null, amount: 574324993, suffix: "" },
    { id: 4, year: "2016", country: "ET", amount: 297337350, suffix: "" },
    { id: 5, year: "2015", country: "ET", amount: 216977566, suffix: "" },
  ];

  const companiesTrained = [
    "Hose Realestate",
    "Reality Realestate",
    "Amibara Realestate",
    "Rg & family realestate",
    "Worthy Homes",
  ];

  // Combined restart logic
  const triggerRestart = useCallback(() => {
    setTimeout(() => {
      setCountedStats({ apartments: 0, totalValue: 0, years: 0, training: 0 });
      setAnimatedAmounts({});
      startCounting();
      startTransactionAmountCounting();
    }, 15000); // 4 second rest gap
  }, []);

  const startCounting = () => {
    const stats = [
      { key: "apartments", target: 17 },
      { key: "totalValue", target: 1 },
      { key: "years", target: 3 },
      { key: "training", target: 4 },
    ];

    stats.forEach((stat) => {
      const duration = 3200;
      const frameRate = 6000 / 60;
      const totalFrames = Math.round(duration / frameRate);
      let frame = 0;

      const timer = setInterval(() => {
        frame++;
        const progress = frame / totalFrames;
        const easeOut = 1 - (1 - progress) * (1 - progress);
        const current = stat.target * easeOut;

        if (frame >= totalFrames) {
          setCountedStats((prev) => ({ ...prev, [stat.key]: stat.target }));
          clearInterval(timer);
        } else {
          setCountedStats((prev) => ({
            ...prev,
            [stat.key]: Math.floor(current),
          }));
        }
      }, frameRate);
    });
  };

  const startTransactionAmountCounting = () => {
    let completed = 0;
    annualTransactions.forEach((transaction, index) => {
      const duration = 1500;
      const frameRate = 1000 / 60;
      const totalFrames = Math.round(duration / frameRate);
      let frame = 0;
      const delay = index * 150;

      setTimeout(() => {
        const timer = setInterval(() => {
          frame++;
          const progress = frame / totalFrames;
          const easeOut = 1 - (1 - progress) * (1 - progress);
          const current = transaction.amount * easeOut;

          if (frame >= totalFrames) {
            setAnimatedAmounts((prev) => ({
              ...prev,
              [transaction.id]: transaction.amount,
            }));
            clearInterval(timer);

            completed++;
            if (completed === annualTransactions.length) {
              triggerRestart();
            }
          } else {
            setAnimatedAmounts((prev) => ({
              ...prev,
              [transaction.id]: Math.floor(current),
            }));
          }
        }, frameRate);
      }, delay);
    });
  };

  useEffect(() => {
    if (!hasAnimated) {
      setHasAnimated(true);
      startCounting();
      startTransactionAmountCounting();
    }
  }, [hasAnimated]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-ET", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getAnimatedAmount = (transactionId) => {
    return animatedAmounts[transactionId] || 0;
  };

  const UAEFlag = () => (
    <svg
      width="18"
      height="9"
      viewBox="0 0 1200 600"
      style={{ borderRadius: "1px" }}
    >
      <rect width="1200" height="600" fill="#00732f" />
      <rect width="1200" height="400" y="200" fill="#fff" />
      <rect width="1200" height="200" y="400" fill="#000" />
      <rect width="300" height="600" fill="#f00" />
    </svg>
  );

  const EthiopiaFlag = () => (
    <svg
      width="18"
      height="9"
      viewBox="0 0 600 300"
      style={{ borderRadius: "1px" }}
    >
      <rect width="600" height="300" fill="#e41e26" />
      <rect width="600" height="200" fill="#ffcf09" />
      <rect width="600" height="100" fill="#048848" />
      <circle cx="300" cy="150" r="45" fill="#0062b1" />
      <path
        d="M300 115 L312 148 L345 148 L318 168 L328 200 L300 180 L272 200 L282 168 L255 148 L288 148 Z"
        fill="#ffcf09"
      />
    </svg>
  );

  const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="#f3b33d">
      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
    </svg>
  );

  const BuildingIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#f3b33d">
      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" />
    </svg>
  );

  const GlobeIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#f3b33d">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
    </svg>
  );

  return (
    <>
      <style>
        {`
          .investment-summary {
            background: linear-gradient(135deg, #0c1120 0%, #0a0f1c 100%);
            min-height: 100vh;
            padding: 5rem 1rem;
            position: relative;
            overflow: hidden;
          }
          .investment-container { max-width: 1100px; margin: 0 auto; }
          .section-header { text-align: center; margin-bottom: 4rem; }
          .section-pre-title { color: #f3b33d; font-size: 0.8rem; font-weight: 400; letter-spacing: 0.2em; text-transform: uppercase; margin-bottom: 0.8rem; display: block; font-family: 'Comfortaa', cursive; }
          .section-header h2 { color: #ffffff; font-size: 2.6rem; font-weight: 400; margin-bottom: 1rem; line-height: 1.2; font-family: 'Cormorant Garamond', serif; }
          .section-header .accent { color: #f3b33d; font-weight: 600; }
          .content-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 3.5rem; align-items: start; }
          .story-text { color: #9aa7b2; font-size: 1rem; line-height: 1.65; margin-bottom: 1.5rem; font-family: 'Comfortaa', cursive; }
          .highlight-stats { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.2rem; margin: 2rem 0; }
          .stat-item { text-align: center; padding: 1.2rem; background: rgba(255, 255, 255, 0.02); border-radius: 10px; border: 1px solid rgba(255, 255, 255, 0.05); transition: all 0.3s ease; display: flex; flex-direction: column; align-items: center; gap: 0.4rem; }
          .stat-item:hover { background: rgba(255, 255, 255, 0.04); transform: translateY(-3px); }
          .stat-number { color: #f3b33d; font-size: 1.8rem; font-weight: 600; font-family: 'Cormorant Garamond', serif; }
          .stat-label { color: #9aa7b2; font-size: 0.85rem; font-weight: 400; text-transform: uppercase; letter-spacing: 0.05em; font-family: 'Comfortaa', cursive; }
          .data-section { margin-bottom: 2.5rem; }
          .data-section h3 { color: #ffffff; font-size: 1.2rem; font-weight: 500; margin-bottom: 1.2rem; font-family: 'Cormorant Garamond', serif; border-bottom: 2px solid rgba(243, 179, 61, 0.2); padding-bottom: 0.4rem; display: flex; align-items: center; gap: 0.5rem; }
          .transactions-list { display: flex; flex-direction: column; gap: 0.8rem; }
          .transaction-item { display: flex; justify-content: space-between; align-items: center; padding: 0.8rem 1rem; background: rgba(255, 255, 255, 0.02); border-radius: 6px; border-left: 3px solid #f3b33d; transition: all 0.3s ease; }
          .transaction-year { color: #f3b33d; font-weight: 500; font-family: 'Comfortaa', cursive; font-size: 0.95rem; display: flex; align-items: center; gap: 0.6rem; }
          .transaction-amount { color: #ffffff; font-weight: 500; font-family: 'Comfortaa', cursive; font-size: 0.95rem; }
          .totally-item { background: rgba(243, 179, 61, 0.08); }
          .company-tag { background: rgba(243, 179, 61, 0.08); color: #f3b33d; padding: 0.6rem 0.8rem; border-radius: 6px; font-size: 0.85rem; font-weight: 500; border: 1px solid rgba(243, 179, 61, 0.15); font-family: 'Comfortaa', cursive; display: flex; align-items: center; gap: 0.4rem; }
          .client-flag { background: rgba(255, 255, 255, 0.04); color: #9aa7b2; padding: 0.5rem 0.9rem; border-radius: 20px; font-size: 0.8rem; border: 1px solid rgba(255, 255, 255, 0.08); display: flex; align-items: center; gap: 0.6rem; font-family: 'Comfortaa', cursive; }
          @media (max-width: 968px) { .content-grid { grid-template-columns: 1fr; gap: 2.5rem; } .section-header h2 { font-size: 2.2rem; } }
        `}
      </style>

      <section className="investment-summary" id="why-me">
        <div className="investment-container">
          <div className="section-header">
            <span className="section-pre-title">The Difference</span>
            <h2>
              Why <span className="accent">Choose Me?</span>
            </h2>
          </div>

          <div className="content-grid">
            <div className="performance-story">
              <p className="story-text">
                When you choose to work with me, you're not just getting a real
                estate advisor — you're gaining a strategic partner with a
                proven track record of delivering exceptional results. My
                approach combines deep market expertise with personalized
                service to ensure your real estate goals are not just met, but
                exceeded.
              </p>

              <p className="story-text">
                With international experience spanning Dubai and Poland, and a
                network of qualified buyers worldwide, I bring global
                perspective to local markets. My digital marketing expertise
                ensures maximum visibility for your properties, while my
                training experience means I understand what drives successful
                real estate teams.
              </p>

              <div className="highlight-stats">
                <div className="stat-item">
                  <BuildingIcon />
                  <div className="stat-number">{countedStats.apartments}+</div>
                  <div className="stat-label">Units Sold/Week</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">
                    {countedStats.totalValue}+ Billion
                  </div>
                  <div className="stat-label">Total Value</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">{countedStats.years}+</div>
                  <div className="stat-label">Years Experience</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">{countedStats.training}</div>
                  <div className="stat-label">Training Sessions</div>
                </div>
              </div>
            </div>

            <div className="performance-data">
              <div className="data-section">
                <h3>
                  <CheckIcon /> Annual Transactions (ETB)
                </h3>
                <div className="transactions-list">
                  {annualTransactions.map((transaction) => (
                    <div
                      key={transaction.id}
                      className={`transaction-item ${
                        transaction.year.includes("Total") ? "totally-item" : ""
                      }`}
                    >
                      <span className="transaction-year">
                        {transaction.country === "ET" && <EthiopiaFlag />}
                        {transaction.country === "AE" && <UAEFlag />}
                        {transaction.year}
                      </span>
                      <span className="transaction-amount">
                        {formatCurrency(getAnimatedAmount(transaction.id))}
                        {transaction.suffix}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="data-section">
                <h3>
                  <CheckIcon /> Sales Training
                </h3>
                <div
                  className="companies-grid"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                    gap: "0.8rem",
                  }}
                >
                  {companiesTrained.map((company, index) => (
                    <div key={index} className="company-tag">
                      <CheckIcon />
                      {company}
                    </div>
                  ))}
                </div>
              </div>

              <div className="data-section">
                <h3>
                  <GlobeIcon /> International Reach
                </h3>
                <div
                  className="international-clients"
                  style={{ display: "flex", flexWrap: "wrap", gap: "0.8rem" }}
                >
                  <span className="client-flag">
                    <UAEFlag /> Dubai Clients
                  </span>
                  <span className="client-flag">
                    <span style={{ fontSize: "1rem" }}>🇵🇱</span> Poland Clients
                  </span>
                  <span className="client-flag">
                    <GlobeIcon /> Global Network
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default InvestmentSummary;
